/**
 * Plain-text input with `#`-triggered searchable Jinja variable suggestions.
 * Used for email subject (and similar single-line fields). Selecting still
 * inserts `{{ name }}` — `#` is only the trigger.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@/components/ui/input';
import { labelForVariable } from '@/lib/emailTemplateVars';
import {
  filterVariableOptions,
  matchHashVariableTriggerInText,
  replaceHashTriggerWithJinja,
} from '@/lib/emailVariableSuggestion';
import { cn } from '@/lib/utils';

export default function EmailVariableHashInput({
  value = '',
  onChange,
  variables = [],
  placeholder = 'Type # to insert a dynamic variable…',
  className,
  'data-testid': testId = 'email-variable-hash-input',
  ...inputProps
}) {
  const inputRef = useRef(null);
  const [suggestion, setSuggestion] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const pendingCaretRef = useRef(null);

  const variableOptions = useMemo(
    () => [...new Set((variables || []).filter(Boolean))].sort(),
    [variables],
  );

  const filteredVariables = useMemo(
    () => (suggestion ? filterVariableOptions(variableOptions, suggestion.query) : []),
    [suggestion, variableOptions],
  );

  const syncSuggestion = (text, caret) => {
    if (!variableOptions.length) {
      setSuggestion(null);
      return;
    }
    const match = matchHashVariableTriggerInText(text, caret);
    if (!match) {
      setSuggestion(null);
      return;
    }
    const el = inputRef.current;
    const rect = el?.getBoundingClientRect();
    setSuggestion({
      query: match.query,
      from: match.from,
      to: match.to,
      left: rect ? rect.left : 0,
      top: rect ? rect.bottom + 4 : 0,
    });
  };

  useEffect(() => {
    if (pendingCaretRef.current == null || !inputRef.current) return;
    const pos = pendingCaretRef.current;
    pendingCaretRef.current = null;
    inputRef.current.setSelectionRange(pos, pos);
  }, [value]);

  useEffect(() => {
    setActiveIndex(0);
  }, [suggestion?.query, suggestion?.from]);

  useEffect(() => {
    if (!suggestion || !filteredVariables.length) return;
    if (activeIndex >= filteredVariables.length) setActiveIndex(0);
  }, [suggestion, filteredVariables, activeIndex]);

  const emitChange = (nextText, caret) => {
    if (typeof caret === 'number') pendingCaretRef.current = caret;
    onChange?.(nextText);
  };

  const insertVariable = (name, range) => {
    if (!name || !range) return;
    const { text: next, caret } = replaceHashTriggerWithJinja(value, range, name);
    setSuggestion(null);
    emitChange(next, caret);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleChange = (e) => {
    const next = e.target.value;
    const caret = e.target.selectionStart ?? next.length;
    emitChange(next, caret);
    syncSuggestion(next, caret);
  };

  const handleSelect = (e) => {
    syncSuggestion(e.target.value, e.target.selectionStart ?? 0);
  };

  const handleKeyDown = (e) => {
    if (!suggestion) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      setSuggestion(null);
      return;
    }

    if (!filteredVariables.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filteredVariables.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filteredVariables.length) % filteredVariables.length);
      return;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const name = filteredVariables[activeIndex];
      if (name) insertVariable(name, suggestion);
    }
  };

  const handleBlur = () => {
    // Delay so option mousedown can fire before the menu unmounts.
    window.setTimeout(() => setSuggestion(null), 150);
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        onClick={handleSelect}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={cn(className)}
        data-testid={testId}
        autoComplete="off"
        {...inputProps}
      />

      {suggestion && typeof document !== 'undefined' && createPortal(
        <div
          role="listbox"
          aria-label="Dynamic variables"
          data-testid={`${testId}-variable-suggestion`}
          className="fixed z-[200] max-h-56 w-72 overflow-auto rounded-md border bg-white p-1 shadow-lg"
          style={{ top: suggestion.top, left: suggestion.left }}
        >
          {filteredVariables.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              No variables match “{suggestion.query}”
            </p>
          ) : (
            filteredVariables.map((name, index) => (
              <button
                key={name}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                data-testid={`${testId}-variable-option-${name}`}
                onMouseDown={(ev) => {
                  ev.preventDefault();
                  insertVariable(name, suggestion);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  'flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left text-xs',
                  index === activeIndex ? 'bg-primary/10 text-foreground' : 'hover:bg-accent',
                )}
              >
                <span className="font-medium text-foreground">{labelForVariable(name)}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{`{{ ${name} }}`}</span>
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
